/* ==========================================================================================
   MammyHug cart — behaviour for the SKIMS-referenced Cart Drawer
   ==========================================================================================

   Three custom elements plus one small listener:

     <cart-toast>         the "Product successfully updated" notice (TZ §20)
     <cart-promo-code>    the Promo Code field (TZ §22-§23)
     <cart-edit-item>     the EDIT ITEM second layer (TZ §12-§21)

   Everything here goes through the AJAX cart Shopify already exposes and then hands the
   resulting cart object to the theme's own "cart:change" event, which is what makes the drawer,
   the bag count and the checkout amount refresh. No parallel cart model is kept in JavaScript,
   and no price or discount is ever computed here — what is rendered is what Shopify returned.

   This file lives outside theme.js on purpose: theme.js is Shopify-owned and a theme rollback
   restores the stock bundle, taking any edits with it. It imports the base classes it needs
   from that bundle through the "theme" import map entry declared in layout/theme.liquid.
   ========================================================================================== */

import { Drawer, extractSectionId } from 'theme';

const routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/* ------------------------------------------------------------------------------------------
   Helpers
   ------------------------------------------------------------------------------------------ */

/**
 * Formats cents using the shop's own money format, so currency symbol, separators and decimals
 * follow the active Shopify Market rather than being guessed here.
 */
function formatMoney(cents) {
  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const format = settings.currencyCodeEnabled
    ? settings.moneyWithCurrencyFormat || settings.moneyFormat
    : settings.moneyFormat;

  if (!format) {
    return (cents / 100).toFixed(2);
  }

  const value = Number(cents) || 0;

  const withDelimiters = (precision, thousands, decimal) => {
    const fixed = (value / 100).toFixed(precision);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    return fraction ? grouped + decimal + fraction : grouped;
  };

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, token) => {
    switch (token) {
      case 'amount':
        return withDelimiters(2, ',', '.');
      case 'amount_no_decimals':
        return withDelimiters(0, ',', '.');
      case 'amount_with_comma_separator':
        return withDelimiters(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return withDelimiters(0, '.', ',');
      case 'amount_with_space_separator':
        return withDelimiters(2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return withDelimiters(0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return withDelimiters(2, "'", '.');
      default:
        return '';
    }
  });
}

/**
 * Collects the section ids that want to be re-rendered with the next cart mutation. The cart
 * drawer answers this event with its own id, which is how it gets fresh markup back in the same
 * round trip as the cart update.
 */
function bundledSections() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );
  return sections;
}

/** Hands a freshly returned cart to the theme so every cart-aware component updates. */
function publishCart(cart, baseEvent) {
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent, cart } })
  );
}

async function postJson(path, body) {
  const response = await fetch(`${routes}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch (error) {
    payload = null;
  }

  return { ok: response.ok, payload };
}

/* ------------------------------------------------------------------------------------------
   Success toast
   ------------------------------------------------------------------------------------------

   A toast normally has to outlive the element that asked for it: EDIT ITEM confirms a change,
   the drawer re-renders, and the element that would have shown the message is replaced in the
   process. The message is therefore parked in this module variable and picked up by whichever
   <cart-toast> connects next.
------------------------------------------------------------------------------------------- */

let pendingToast = null;

const CHECK_ICON =
  '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
  '<path d="m3.5 8.5 3 3 6-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class CartToast extends HTMLElement {
  connectedCallback() {
    if (pendingToast) {
      const message = pendingToast;
      pendingToast = null;
      this.show(message);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timeout);
  }

  show(message) {
    this.innerHTML = CHECK_ICON;
    const label = document.createElement('span');
    label.textContent = message;
    this.appendChild(label);
    clearTimeout(this.timeout);

    // Next frame, so the transition runs instead of the element appearing already visible. The
    // auto-hide is armed from inside that frame on purpose: a background tab runs setTimeout but
    // not requestAnimationFrame, so arming it outside lets the hide land before the show.
    requestAnimationFrame(() => {
      this.setAttribute('data-visible', '');
      this.timeout = setTimeout(() => {
        this.removeAttribute('data-visible');
      }, 2000);
    });
  }
}

/**
 * Parks a toast for the next <cart-toast> that connects. Showing it on the element that
 * is on screen right now would be wrong: a cart change is about to replace the drawer's markup,
 * taking that element with it. The timeout is the safety net for the case where no re-render
 * follows after all.
 */
function queueToast(message) {
  pendingToast = message;

  setTimeout(() => {
    if (pendingToast !== message) return;
    pendingToast = null;
    document.querySelector('cart-toast')?.show(message);
  }, 600);
}

if (!window.customElements.get('cart-toast')) {
  window.customElements.define('cart-toast', CartToast);
}

/* ------------------------------------------------------------------------------------------
   Promo code
   ------------------------------------------------------------------------------------------

   Shopify's AJAX cart takes a discount code on /cart/update.js and answers with the cart. The
   cart's discount_codes entries carry an "applicable" flag, and that flag — not any check of our
   own — is what decides whether the shopper sees an applied state or an error. A code Shopify
   refuses (unknown, expired, not combinable with the running automatic discount, not applicable
   to this cart) comes back as applicable: false and is reported as such.
------------------------------------------------------------------------------------------- */

class CartPromoCode extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.cart-promo-code__input');
    this.submitButton = this.querySelector('.cart-promo-code__submit');
    this.messageElement = this.querySelector('.cart-promo-code__message');

    this.submitButton?.addEventListener('click', () => this.apply());

    this.input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.apply();
      }
    });

    this.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-remove-discount]');
      if (removeButton) {
        this.removeCode(removeButton.getAttribute('data-remove-discount'));
      }
    });
  }

  get strings() {
    if (!this._strings) {
      try {
        this._strings = JSON.parse(this.querySelector('[data-promo-strings]')?.textContent || '{}');
      } catch (error) {
        this._strings = {};
      }
    }

    return this._strings;
  }

  setMessage(text, state) {
    if (!this.messageElement) return;

    this.messageElement.textContent = text || '';
    this.messageElement.hidden = !text;

    if (state) {
      this.messageElement.setAttribute('data-state', state);
    } else {
      this.messageElement.removeAttribute('data-state');
    }
  }

  async apply() {
    const code = (this.input?.value || '').trim();

    if (!code) {
      this.setMessage(this.strings.empty, 'error');
      this.input?.focus();
      return;
    }

    await this.send(code, (cart) => {
      const entry = (cart.discount_codes || []).find(
        (discount) => (discount.code || '').toLowerCase() === code.toLowerCase()
      );

      if (!entry || entry.applicable === false) {
        this.setMessage(this.strings.invalid, 'error');
        return false;
      }

      // Confirmation goes to the toast rather than to this element's own message line: the
      // drawer is about to re-render with the new totals, which replaces this whole block.
      queueToast(this.strings.applied);
      return true;
    });
  }

  async removeCode(code) {
    // The discount parameter replaces the whole set rather than adding to it, so a code is
    // dropped by re-sending the others. The codes currently on the cart are the ones Liquid
    // just rendered, which is why they are read back out of the markup.
    const remaining = Array.from(this.querySelectorAll('[data-remove-discount]'))
      .map((button) => button.getAttribute('data-remove-discount'))
      .filter((entry) => entry.toLowerCase() !== code.toLowerCase())
      .join(',');

    await this.send(remaining, () => true);
  }

  /**
   * Sends the code, then decides — through the caller's inspector — whether Shopify accepted it.
   * The drawer is only re-rendered when it did, so a rejected code leaves the field open with its
   * error next to it, rather than collapsing the block the shopper is still reading.
   */
  async send(code, inspect) {
    if (this.hasAttribute('aria-busy')) return;

    this.setAttribute('aria-busy', 'true');
    this.setMessage('', null);
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sections = bundledSections();
    const { ok, payload } = await postJson('cart/update.js', {
      discount: code,
      sections: sections.join(',')
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    this.removeAttribute('aria-busy');

    if (!ok || !payload) {
      this.setMessage(this.strings.error, 'error');
      return;
    }

    if (inspect(payload)) {
      publishCart(payload, 'promo-code:change');
    }
  }
}

if (!window.customElements.get('cart-promo-code')) {
  window.customElements.define('cart-promo-code', CartPromoCode);
}

/* ------------------------------------------------------------------------------------------
   EDIT ITEM
   ------------------------------------------------------------------------------------------

   Opens over the drawer, reads the product from /products/<handle>.js, and lets the shopper pick
   a different combination. Selecting does not touch the cart — only CONFIRM CHANGES does, which
   is the behaviour TZ §16 asks for.

   Extending Drawer gives this the same shell, animation, focus trap and close button as the cart
   drawer itself. focus-trap stacks traps, so while this layer is open the drawer's trap is
   paused and Tab cannot reach the bag underneath.
------------------------------------------------------------------------------------------- */

/* ------------------------------------------------------------------------------------------
   Variant rules, shared by <cart-edit-item> and <cart-quick-add>
   ------------------------------------------------------------------------------------------

   Both elements let a shopper assemble an option combination and both have to answer the same two
   questions about it: does this combination exist, and is a given option value still worth
   offering. Written once here, and both describe a variant the same way --
   { id, options: [...], available } -- so neither grows its own idea of the catalogue.

   Availability is always Shopify's own `available`, never inferred. */

/** The variant for a fully specified combination, or null when Shopify does not sell it. */
function findVariant(variants, selection) {
  return variants.find((variant) => variant.options.every((value, index) => value === selection[index])) || null;
}

/**
 * Whether choosing `value` at `position` can still lead to something in stock, given whatever
 * else is already chosen. Positions still empty are left open rather than treated as a mismatch,
 * so the first choice never disables the second one before it is made.
 */
function hasAvailableCombination(variants, selection, position, value) {
  return variants.some((variant) => {
    if (variant.options[position] !== value) return false;
    if (variant.available === false) return false;

    return selection.every(
      (chosen, index) => index === position || chosen == null || variant.options[index] === chosen
    );
  });
}

/** Parses a <script type="application/json"> payload, falling back rather than throwing. */
function readJson(element, fallback) {
  try {
    return JSON.parse(element?.textContent || '') ?? fallback;
  } catch (error) {
    return fallback;
  }
}

const COLOR_OPTION_NAMES = ['color', 'colour', 'couleur', 'farbe', 'colore', 'kleur', 'цвет', 'колір'];

class CartEditItem extends Drawer {
  connectedCallback() {
    super.connectedCallback();

    this.productElement = this.querySelector('[data-edit-product]');
    this.optionsElement = this.querySelector('[data-edit-options]');
    this.loadingElement = this.querySelector('[data-edit-loading]');
    this.errorElement = this.querySelector('[data-edit-error]');
    this.confirmButton = this.querySelector('[data-edit-confirm]');

    try {
      this.strings = JSON.parse(this.querySelector('[data-edit-strings]')?.textContent || '{}');
    } catch (error) {
      this.strings = {};
    }

    this.confirmButton?.addEventListener('click', () => this.confirm());

    this.openListener = (event) => {
      const trigger = event.target.closest?.(`[data-cart-edit="${this.id}"]`);
      if (!trigger) return;

      event.preventDefault();
      this.openFor(trigger);
    };

    document.addEventListener('click', this.openListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.openListener);
  }

  /** The drawer under this layer must stay put, so a click on it is not treated as "dismiss". */
  get clickOutsideDeactivates() {
    return false;
  }

  async openFor(trigger) {
    try {
      this.line = JSON.parse(trigger.getAttribute('data-cart-edit-payload'));
    } catch (error) {
      return;
    }

    this.product = null;
    this.selection = null;

    if (this.optionsElement) this.optionsElement.innerHTML = '';
    if (this.productElement) this.productElement.hidden = true;
    if (this.loadingElement) this.loadingElement.hidden = false;
    this.setError('');
    this.confirmButton?.setAttribute('disabled', '');

    // The panel fetches while it animates open rather than after it has finished. show()
    // resolves on the drawer's "dialog:after-show" event, so awaiting it first leaves EDIT ITEM
    // stuck on "Loading..." for as long as that event does not arrive -- an interrupted or
    // skipped transition is enough -- and delays the request for no reason when it does.
    const shown = this.show();
    await this.load();
    return shown;
  }

  async load() {
    try {
      const response = await fetch(`${routes}products/${this.line.handle}.js`);
      if (!response.ok) throw new Error('product not found');

      this.product = await response.json();
    } catch (error) {
      if (this.loadingElement) this.loadingElement.hidden = true;
      this.setError(this.strings.genericError);
      return;
    }

    if (this.loadingElement) this.loadingElement.hidden = true;

    const currentVariant =
      this.product.variants.find((variant) => String(variant.id) === String(this.line.variantId)) ||
      this.product.variants[0];

    this.selection = (currentVariant.options || []).slice();
    this.render();
  }

  get optionNames() {
    return (this.product.options || []).map((option) => (typeof option === 'string' ? option : option.name));
  }

  optionValues(position) {
    const declared = this.product.options?.[position];

    if (declared && Array.isArray(declared.values) && declared.values.length > 0) {
      return declared.values;
    }

    // Older /products.js payloads describe options as plain names, so the values are recovered
    // from the variants — in variant order, which is the merchant's own order.
    return [...new Set(this.product.variants.map((variant) => variant.options[position]))];
  }

  /** The variant for a combination, or undefined when the combination does not exist at all. */
  variantFor(optionValues) {
    return findVariant(this.product.variants, optionValues) || undefined;
  }

  get selectedVariant() {
    return this.selection ? this.variantFor(this.selection) : undefined;
  }

  render() {
    this.renderProduct();
    this.renderOptions();
    this.updateConfirmState();
  }

  renderProduct() {
    if (!this.productElement) return;

    const variant = this.selectedVariant;
    const image =
      variant?.featured_image?.src || this.product.featured_image || this.product.images?.[0] || '';
    const price = variant ? variant.price : this.product.price;

    this.productElement.replaceChildren();

    if (image) {
      const img = document.createElement('img');
      img.className = 'cart-edit-item__product-image';
      img.src = image;
      img.alt = '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      this.productElement.appendChild(img);
    } else {
      this.productElement.appendChild(document.createElement('span'));
    }

    const details = document.createElement('div');

    const title = document.createElement('p');
    title.className = 'cart-edit-item__product-title h6';
    title.textContent = this.product.title;

    const priceElement = document.createElement('p');
    priceElement.className = 'cart-edit-item__product-price';
    priceElement.textContent = formatMoney(price);

    details.append(title, priceElement);
    this.productElement.appendChild(details);
    this.productElement.hidden = false;
  }

  renderOptions() {
    if (!this.optionsElement) return;

    this.optionsElement.innerHTML = '';

    this.optionNames.forEach((name, position) => {
      const isColor = COLOR_OPTION_NAMES.includes(name.trim().toLowerCase());

      const group = document.createElement('div');
      group.className = 'cart-edit-item__option';

      const label = document.createElement('p');
      label.className = 'cart-edit-item__option-label';

      const optionName = document.createElement('span');
      optionName.className = 'cart-edit-item__option-name';
      optionName.textContent = name;

      const currentValue = document.createElement('span');
      currentValue.className = 'cart-edit-item__option-current';
      currentValue.textContent = this.selection[position] || '';

      label.append(optionName, currentValue);
      group.appendChild(label);

      const list = document.createElement('div');
      list.className = 'cart-edit-item__values';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-label', name);

      this.optionValues(position).forEach((value) => {
        const candidate = this.selection.slice();
        candidate[position] = value;

        const variant = this.variantFor(candidate);
        const selected = this.selection[position] === value;
        const swatchSrc = isColor ? this.swatchFor(position, value) : null;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cart-edit-item__value' + (swatchSrc ? ' cart-edit-item__value--swatch' : '');
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        button.dataset.position = String(position);
        button.dataset.value = value;

        if (swatchSrc) {
          button.style.backgroundImage = `url(${swatchSrc})`;
          const swatchLabel = document.createElement('span');
          swatchLabel.className = 'sr-only';
          swatchLabel.textContent = value;
          button.appendChild(swatchLabel);
        } else {
          button.textContent = value;
        }

        // A combination Shopify does not sell, or reports as out of stock, cannot be chosen.
        if (!variant || variant.available === false) {
          button.disabled = true;
          button.setAttribute(
            'aria-label',
            `${value} — ${variant ? this.strings.soldOut || '' : this.strings.sizeUnavailable || ''}`.trim()
          );
        } else {
          button.addEventListener('click', () => this.select(position, value));
        }

        list.appendChild(button);
      });

      group.appendChild(list);
      this.optionsElement.appendChild(group);
    });
  }

  /** Uses the image of a variant carrying this value as its swatch — real product imagery. */
  swatchFor(position, value) {
    const variant = this.product.variants.find(
      (candidate) => candidate.options[position] === value && candidate.featured_image?.src
    );
    return variant ? variant.featured_image.src : null;
  }

  select(position, value) {
    this.selection[position] = value;
    this.setError('');
    this.render();
  }

  updateConfirmState() {
    if (!this.confirmButton) return;

    const variant = this.selectedVariant;
    const changed = variant && String(variant.id) !== String(this.line.variantId);
    const usable = Boolean(variant && variant.available !== false && changed);

    this.confirmButton.disabled = !usable;
  }

  setError(message) {
    if (!this.errorElement) return;

    this.errorElement.textContent = message || '';
    this.errorElement.hidden = !message;
  }

  /**
   * Applies the change. Shopify's cart API cannot swap the variant of an existing line, so the
   * new one is added and the old one removed — in that order, and with no re-render in between,
   * so the shopper never sees the cart holding both.
   */
  async confirm() {
    const variant = this.selectedVariant;
    if (!variant || this.hasAttribute('aria-busy')) return;

    this.setAttribute('aria-busy', 'true');
    this.confirmButton.disabled = true;
    this.setError('');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const addBody = {
      items: [
        {
          id: variant.id,
          quantity: this.line.quantity
        }
      ]
    };

    // Liquid renders a line that carries no properties as [], and /cart/add.js refuses an array
    // there ("expected Array to be a Hash: properties"), so the key is sent only when there is
    // something to send -- which is also what keeps a line with real properties intact.
    const properties = this.line.properties;
    if (properties && !Array.isArray(properties) && Object.keys(properties).length > 0) {
      addBody.items[0].properties = properties;
    }

    if (this.line.sellingPlan) {
      addBody.items[0].selling_plan = this.line.sellingPlan;
    }

    const added = await postJson('cart/add.js', addBody);

    if (!added.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this.removeAttribute('aria-busy');
      this.setError(added.payload?.description || this.strings.genericError);
      this.updateConfirmState();
      return;
    }

    /* Shopify re-hashes every line key whenever the cart changes, so the key this panel was
       rendered with went stale the moment the line above was added -- passing it to change.js
       answers "no valid id or line parameter" and the edit fails every time. The line to remove
       is looked up again by its variant, and its current key is what gets sent. */
    const addedKey = added.payload?.key;
    let removeKey = this.line.lineKey;

    try {
      const current = await (await fetch(routes + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      const stale = current?.items?.find(
        (item) => String(item.id) === String(this.line.variantId) && item.key !== addedKey
      );
      if (stale) removeKey = stale.key;
    } catch (error) {
      // Falling back to the rendered key: no worse than not looking.
    }

    const sections = bundledSections();
    const removed = await postJson('cart/change.js', {
      id: removeKey,
      quantity: 0,
      sections: sections.join(',')
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    this.removeAttribute('aria-busy');

    if (!removed.ok || !removed.payload) {
      // The new line is in the cart but the old one is not gone. Refreshing is the honest thing
      // to do: the drawer must show what Shopify actually holds, not what was intended.
      this.setError(this.strings.genericError);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.updateConfirmState();
      return;
    }

    // Closed before the drawer is told to re-render, so the layer is not replaced mid-animation.
    // The wait is capped: hide() resolves on "dialog:after-hide", which never arrives while the
    // tab is in the background, and without a cap the toast and the re-render below would never
    // run -- leaving the bag showing the variant the shopper just changed away from.
    await Promise.race([this.hide(), new Promise((resolve) => setTimeout(resolve, 500))]);

    queueToast(this.strings.successToast || '');
    publishCart(removed.payload, 'cart-edit-item:change');
  }
}

if (!window.customElements.get('cart-edit-item')) {
  window.customElements.define('cart-edit-item', CartEditItem);
}

/* ------------------------------------------------------------------------------------------
   Quick add on a cross-sell card
   ------------------------------------------------------------------------------------------

   One <select> per product option and an add button under each recommended card, so a shopper can
   take a recommendation without leaving the bag. Cart page spec §14 describes what has to happen
   after Add to Bag from Frequently Bought Together, which is what this drives.

   Cart Drawer TZ §35 rules out "a separate complex Quick Add just for Cross-sell", and that is the
   reason this class is as short as it is: the rules about which combinations exist and which
   option values are still reachable come from the helpers <cart-edit-item> uses. This element
   owns a selector and a button, not a second model of the catalogue.

   Variants arrive rendered in the card (snippets/cart-quick-add.liquid) rather than fetched: the
   row holds up to ten cards, and ten /products/<handle>.js requests per drawer open is not a
   trade worth making against the work already done on page weight.
   ------------------------------------------------------------------------------------------ */

class CartQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.initialised) return;
    this.initialised = true;

    this.selects = Array.from(this.querySelectorAll('[data-quick-add-option]'));
    this.button = this.querySelector('[data-quick-add-submit]');
    this.messageElement = this.querySelector('[data-quick-add-message]');
    this.singleVariantId = this.querySelector('[data-quick-add-variant]')?.dataset.quickAddVariant || null;

    this.strings = readJson(this.querySelector('[data-quick-add-strings]'), {});
    this.variants = readJson(this.querySelector('[data-quick-add-variants]'), []).map((entry) => ({
      id: entry[0],
      // Liquid renders an absent option as null; dropping them keeps options[] the length the
      // product actually has, which is what the selectors are indexed by.
      options: [entry[1], entry[2], entry[3]].filter((value) => value !== null && value !== undefined),
      available: entry[4] === 1
    }));

    this.selects.forEach((select) => select.addEventListener('change', () => this.refresh()));
    this.button?.addEventListener('click', () => this.add());

    if (this.selects.length > 0) this.refresh();
  }

  /** What is chosen right now, with null for anything still on its placeholder. */
  get selection() {
    return this.selects.map((select) => select.value || null);
  }

  get selectedVariant() {
    const selection = this.selection;
    if (selection.some((value) => value == null)) return null;
    return findVariant(this.variants, selection);
  }

  refresh() {
    const selection = this.selection;

    // Values that cannot lead to anything in stock are greyed out as the shopper narrows down,
    // rather than being offered and then refused at the button.
    this.selects.forEach((select, position) => {
      Array.from(select.options).forEach((option) => {
        if (option.value === '') return;
        option.disabled = !hasAvailableCombination(this.variants, selection, position, option.value);
      });
    });

    const complete = selection.every((value) => value != null);
    const variant = complete ? findVariant(this.variants, selection) : null;

    if (this.button) this.button.disabled = !variant || !variant.available;

    if (!complete) {
      this.setMessage('');
    } else if (!variant) {
      this.setMessage(this.strings.unavailable || '');
    } else if (!variant.available) {
      this.setMessage(this.strings.soldOut || '');
    } else {
      this.setMessage('');
    }
  }

  setMessage(message) {
    if (!this.messageElement) return;

    this.messageElement.textContent = message || '';
    this.messageElement.hidden = !message;
  }

  async add() {
    const variant = this.selectedVariant;
    const id = this.singleVariantId || (variant && variant.available ? variant.id : null);

    // The aria-busy guard is what stops a second click from queueing a second add (spec §21.6).
    if (!id || this.hasAttribute('aria-busy')) return;

    this.setAttribute('aria-busy', 'true');
    if (this.button) this.button.disabled = true;
    this.setMessage('');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sections = bundledSections();
    const added = await postJson('cart/add.js', {
      items: [{ id, quantity: 1 }],
      sections: sections.join(',')
    });

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    this.removeAttribute('aria-busy');

    if (!added.ok) {
      // Shopify's own description is already localised and says why -- out of stock, limit
      // reached -- so it is preferred over anything this theme could word.
      this.setMessage(added.payload?.description || this.strings.genericError || '');
      this.refresh();
      return;
    }

    // /cart/add.js answers with the line it added, not with the cart, so the cart is read back
    // and the sections rendered by the add are carried across -- the same two steps the theme's
    // own product form takes.
    let cart = null;
    try {
      cart = await (await fetch(routes + 'cart.js', { headers: { Accept: 'application/json' } })).json();
    } catch (error) {
      cart = null;
    }

    if (!cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    cart.sections = added.payload?.sections;

    // Deliberately not "variant:add": that is the event the theme slides the drawer open on, and
    // this add happens inside the drawer, or on /cart, where it is already in front of the
    // shopper. The re-render that follows drops this card from the row, because the cross-sell
    // excludes what is already in the bag -- which is spec §14's "refresh recommendation
    // exclusions", done by the same Liquid that built the row in the first place.
    publishCart(cart, 'cart-quick-add:add');
  }
}

if (!window.customElements.get('cart-quick-add')) {
  window.customElements.define('cart-quick-add', CartQuickAdd);
}

/* ------------------------------------------------------------------------------------------
   /cart page
   ------------------------------------------------------------------------------------------

   Keeps the cart page in step with the cart without reloading it. The mechanism is the one the
   drawer already uses: announce this section id when a mutation is about to be sent, then swap
   in the markup Shopify returns with the updated cart. Because the whole section is replaced,
   the totals, the promotion line, the discount rows, the recommendations and their exclusions
   are all recomputed by Liquid from the real cart — nothing is patched by hand in the DOM.

   Three reload paths in theme.js had to be patched for this to be reachable at all; see
   .claude/apply-customizations.js, "cart page AJAX".
------------------------------------------------------------------------------------------- */

class CartPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.sectionId || extractSectionId(this);
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    document.addEventListener('cart:prepare-bundled-sections', (event) => {
      event.detail.sections.push(this.sectionId);
    }, { signal });

    document.addEventListener('cart:change', (event) => {
      this.replaceContent(event.detail.cart?.sections?.[this.sectionId]);
    }, { signal });

    document.addEventListener('cart:refresh', () => this.refresh(), { signal });

    // Coming back with the browser's back button serves a cached page, which can show a cart the
    // shopper has since changed in another tab.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh();
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async refresh() {
    const response = await fetch(`${routes}?section_id=${this.sectionId}`);
    this.replaceContent(await response.text());
  }

  replaceContent(html) {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed
      .getElementById(`shopify-section-${this.sectionId}`)
      ?.querySelector('cart-page');

    if (!fresh) return;

    // An empty cart changes the page structure entirely (and drops the checkout form), so the
    // simplest correct thing is to let the server render it and land on it properly.
    const wasEmpty = this.querySelector('.cart-layout') === null;
    const isEmpty = fresh.querySelector('.cart-layout') === null;

    this.replaceChildren(...fresh.children);

    if (wasEmpty !== isEmpty) {
      this.dispatchEvent(new CustomEvent('cart-page:emptied', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('cart-page')) {
  window.customElements.define('cart-page', CartPage);
}

/* ------------------------------------------------------------------------------------------
   Continue Shopping
   ------------------------------------------------------------------------------------------

   Points the link back at where the shopper was browsing, when that is known and is a page of
   this storefront. document.referrer is the honest source for that: it is same-origin, it is not
   /cart itself, and it is not a checkout URL. Anything else leaves the Liquid fallback in place,
   so the link always goes somewhere real and no new page exists just for this.
------------------------------------------------------------------------------------------- */

class CartContinue extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a');
    if (!link || !document.referrer) return;

    let referrer;
    try {
      referrer = new URL(document.referrer);
    } catch (error) {
      return;
    }

    if (referrer.origin !== window.location.origin) return;
    if (referrer.pathname.replace(/\/$/, '').endsWith('/cart')) return;
    if (referrer.pathname.startsWith('/checkouts')) return;

    link.href = referrer.href;
  }
}

if (!window.customElements.get('cart-continue')) {
  window.customElements.define('cart-continue', CartContinue);
}

/* ------------------------------------------------------------------------------------------
   Line busy state
   ------------------------------------------------------------------------------------------

   The theme's <line-item-quantity> announces "line-item:will-change" before it posts. Marking
   the line busy dims it and, through the CSS rule, makes it inert — which is what stops a second
   click on plus from queueing a second cart mutation while the first is still in flight.
------------------------------------------------------------------------------------------- */

document.addEventListener('line-item:will-change', (event) => {
  event.target.setAttribute?.('aria-busy', 'true');
});

/* ------------------------------------------------------------------------------------------
   Dragging the cross-sell row with a mouse
   ------------------------------------------------------------------------------------------

   The theme carousel has an allow-drag of its own, but on release it scrolls back to the cell it
   believes is selected -- and after a backwards drag that is the cell the shopper came from, so
   the row springs forward again and will not go back. This does the same job without the memory.

   While the drag runs, snapping is off and the row follows the cursor one to one. On release the
   speed of the last few moves is carried forward for a moment, the nearest card to where that
   lands is chosen, and the row is scrolled there smoothly -- so a flick travels and a slow drag
   settles where it was left. Snapping is handed back only once that scroll has finished, because
   restoring it mid-flight would cut the animation short.

   Mouse only. Touch keeps its native scrolling, which already does all of this.
------------------------------------------------------------------------------------------- */

const DRAG_THRESHOLD = 4;
const FLICK_CARRY_MS = 140;

let dragList = null;
let dragStartX = 0;
let dragStartLeft = 0;
let dragMoved = false;
let swallowClick = false;
let lastPointerX = 0;
let lastPointerTime = 0;
let dragVelocity = 0;

/** The scroll position at which the card nearest to `position` sits against the left edge. */
function nearestCardStart(list, position) {
  const listLeft = list.getBoundingClientRect().left;
  const limit = list.scrollWidth - list.clientWidth;
  let best = position;
  let bestDistance = Infinity;

  for (const card of list.children) {
    const start = list.scrollLeft + card.getBoundingClientRect().left - listLeft;
    const distance = Math.abs(start - position);

    if (distance < bestDistance) {
      bestDistance = distance;
      best = start;
    }
  }

  return Math.max(0, Math.min(best, limit));
}

function restoreSnappingWhenSettled(list) {
  let restored = false;
  const restore = () => {
    if (restored) return;
    restored = true;
    list.style.removeProperty('scroll-snap-type');
  };

  // scrollend is the accurate signal; the timeout covers browsers that do not fire it, and the
  // case where the release does not move the row at all.
  list.addEventListener('scrollend', restore, { once: true });
  setTimeout(restore, 700);
}

document.addEventListener('pointerdown', (event) => {
  swallowClick = false;

  if (event.button !== 0 || event.pointerType !== 'mouse') return;

  const list = event.target.closest?.('.cart-cross-sell__list');
  if (!list || list.scrollWidth <= list.clientWidth) return;

  dragList = list;
  dragStartX = event.clientX;
  dragStartLeft = list.scrollLeft;
  dragMoved = false;
  lastPointerX = event.clientX;
  lastPointerTime = event.timeStamp;
  dragVelocity = 0;
});

document.addEventListener('pointermove', (event) => {
  if (!dragList) return;

  const distance = event.clientX - dragStartX;

  // Below the threshold this is still a click, and the row must not creep under a steady hand.
  if (!dragMoved) {
    if (Math.abs(distance) < DRAG_THRESHOLD) return;
    dragMoved = true;
    dragList.style.setProperty('scroll-snap-type', 'none');

    try {
      dragList.setPointerCapture(event.pointerId);
    } catch (error) {
      // A capture the browser will not grant is not worth failing the drag over.
    }
  }

  const elapsed = event.timeStamp - lastPointerTime;

  if (elapsed > 0) {
    // Smoothed, so one stuttering sample cannot decide where the row lands.
    dragVelocity = dragVelocity * 0.7 + ((event.clientX - lastPointerX) / elapsed) * 0.3;
    lastPointerX = event.clientX;
    lastPointerTime = event.timeStamp;
  }

  dragList.scrollLeft = dragStartLeft - distance;

  // Also what stops the browser starting a native image drag from the product photos.
  event.preventDefault();
});

function endCrossSellDrag() {
  if (!dragList) return;

  const list = dragList;
  dragList = null;

  if (!dragMoved) return;

  dragMoved = false;
  swallowClick = true;

  const limit = list.scrollWidth - list.clientWidth;
  const carried = list.scrollLeft - dragVelocity * FLICK_CARRY_MS;
  const projected = Math.max(0, Math.min(carried, limit));

  list.scrollTo({ left: nearestCardStart(list, projected), behavior: 'smooth' });
  restoreSnappingWhenSettled(list);
}

document.addEventListener('pointerup', endCrossSellDrag);
document.addEventListener('pointercancel', endCrossSellDrag);

/* A drag that finishes on a card must not open it. */
document.addEventListener(
  'click',
  (event) => {
    if (!swallowClick) return;
    swallowClick = false;
    if (!event.target.closest?.('.cart-cross-sell__list')) return;
    event.preventDefault();
    event.stopPropagation();
  },
  true
);
